import { FormEvent, useEffect, useState } from "react";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminListMessages,
  adminSendMessage,
  type ConferenceAdminMessage,
} from "../api";
import { PageHeader } from "../shells/PageHeader";
import { validateAdminMessageForm } from "./adminMessageForm";

export function AdminMessagesPage() {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [history, setHistory] = useState<ConferenceAdminMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const reload = () =>
    adminListMessages({ limit: 50 })
      .then((rows) => setHistory(rows))
      .catch(() => setHistory([]));

  useEffect(() => {
    reload().catch(() => undefined);
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    const errors = validateAdminMessageForm(
      { email, subject, body },
      {
        email: t.admin.msgNeedEmail,
        subject: t.admin.msgNeedSubject,
        body: t.admin.msgNeedBody,
        invalidEmail: t.admin.msgInvalidEmail,
      },
    );
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setBusy(true);
    try {
      await adminSendMessage({
        email: email.trim(),
        subject: subject.trim(),
        body: body.trim(),
      });
      setMessage(t.admin.msgSent);
      setEmail("");
      setSubject("");
      setBody("");
      setFieldErrors({});
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title={t.admin.messages} lead={t.admin.messagesLead} backTo="/admin" />
      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {message && (
        <div className="dn-alert" style={{ background: "rgba(15,79,58,0.08)" }}>
          {message}
        </div>
      )}

      <form className="dn-card dn-form" onSubmit={onSubmit} style={{ marginBottom: "1.5rem" }}>
        <h3>{t.admin.msgCompose}</h3>
        <p className="dn-muted">{t.admin.msgComposeHelp}</p>

        <label>
          {t.admin.msgRecipientEmail}
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            disabled={busy}
            onChange={(ev) => setEmail(ev.target.value)}
            placeholder="name@example.com"
          />
          {fieldErrors.email && <span className="dn-corr-field-error">{fieldErrors.email}</span>}
        </label>

        <label>
          {t.admin.msgSubject}
          <input
            type="text"
            required
            value={subject}
            disabled={busy}
            onChange={(ev) => setSubject(ev.target.value)}
            maxLength={512}
          />
          {fieldErrors.subject && <span className="dn-corr-field-error">{fieldErrors.subject}</span>}
        </label>

        <label>
          {t.admin.msgBody}
          <textarea
            required
            rows={8}
            value={body}
            disabled={busy}
            onChange={(ev) => setBody(ev.target.value)}
            maxLength={20000}
          />
          {fieldErrors.body && <span className="dn-corr-field-error">{fieldErrors.body}</span>}
        </label>

        <button className="dn-btn dn-btn-primary" type="submit" disabled={busy}>
          {busy ? t.admin.msgSending : t.admin.msgSend}
        </button>
      </form>

      <h2>{t.admin.msgHistory}</h2>
      <p className="dn-muted">{t.admin.msgHistoryLead}</p>
      <div className="dn-table-wrap">
        <table className="dn-table dn-table-responsive">
          <thead>
            <tr>
              <th>{t.admin.msgColRecipient}</th>
              <th>{t.admin.msgColSubject}</th>
              <th>{t.admin.msgColSentAt}</th>
              <th>{t.admin.colStatus}</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 && (
              <tr>
                <td colSpan={4}>{t.admin.msgHistoryEmpty}</td>
              </tr>
            )}
            {history.map((row) => (
              <tr key={row.id}>
                <td data-label={t.admin.msgColRecipient}>{row.recipient_email}</td>
                <td data-label={t.admin.msgColSubject}>{row.subject}</td>
                <td data-label={t.admin.msgColSentAt}>{row.sent_at || row.created_at || "—"}</td>
                <td data-label={t.admin.colStatus}>
                  {row.status}
                  {row.error_message ? ` · ${row.error_message}` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
